import React, { useState, useEffect } from "react";

// Components
import { UserAddOutlined } from "@ant-design/icons";
import Loader from "components/Loader/Loader";
import InputWithVerification from "./InputWithVerification";
import { Select, Alert, notification, Button, Spin, TreeSelect, AutoComplete } from "antd";
import FulfillmentBackButton from "../../../fulfillment/FulfillmentBackButton";

// Helpers
import User from "lib/regular/user";
import { startCase, isEmpty, get, orderBy, upperCase, uniq } from "lodash-es";
import Organization from "lib/organization";
import { phone as phoneValidator } from "phone";

// Hooks
import { useOrganizationUser, useOrganizationDetails } from "hooks/api/fulfillment/organization";
import { useHistory, useParams } from "react-router";
import { useUserDetails } from "hooks/api/common/user";
import { useRolesIndex } from "hooks/api/common/roles";
import useUser from "../../../../hooks/reduxContainer/_common/useUser";

// Interfaces
import { Role } from "../../../../interfaces/common/role";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlagQuery";
import { fetchCountryIndex } from "../../../../api/common/country";
import { useQuery } from "react-query";
import { useUpdateDriverUser } from "hooks/api/common/useUpdateDriverUser";
import { ServiceArea } from "@secondcloset/types/src/_common";
import { useServiceArea } from "hooks/api/common/useServiceArea";
import { useFleetOperators } from "hooks/api/common/useFleetOperators";

interface Params {
  organization_id: string;
  user_id?: string;
}

const OrganizationUserCreateEditPage = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState<string | null>("");
  const [roleID, setRoleID] = useState("");
  const [inputError, setInputError] = useState("");
  const [subrole, setSubrole] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [dspType, setDspType] = useState<string>("");
  const [selectedServiceAreas, setSelectedServiceAreas] = useState<ServiceArea[]>([]);
  const [driverUserId, setDriverUserId] = useState("");
  const [positionId, setPositionId] = useState("");
  const { organizationDetails, fetchOrganizationDetails } = useOrganizationDetails();
  const { createOrganizationUser, organizationUserDetails } = useOrganizationUser();
  const { userDetails, fetchUserDetails, updateUserDetails } = useUserDetails();
  const { fetchRolesIndex, rolesIndex } = useRolesIndex();
  const isGoBolt = Organization.isGoBolt(organizationDetails.data?.name);
  const history = useHistory();
  const params: Params = useParams();
  const organizationID = params?.organization_id || "";
  const userID = params?.user_id || "";
  const isEditingUser = !!userID;
  const query = { organization_id: organizationID };
  const [phonePrefix, setPhonePrefix] = useState("CAN");
  const { updateDriverUser, isLoading: isUpdateDriverUserLoading } = useUpdateDriverUser();
  const { serviceArea } = useServiceArea();
  const { data: fleetOperatorList } = useFleetOperators();

  const isDriverUserExist = !!driverUserId;

  const shouldShowDriverAttributes = isDriverUserExist && isEditingUser && isGoBolt;

  const { user } = useUser();
  const enableCreateUser = useFeatureFlagQuery("enable-create-user", {
    user_id: user.id,
    organization_id: organizationID,
  });

  const countryIndex = useQuery(["countries"], fetchCountryIndex, {
    enabled: !!organizationID,
    select: (result) => {
      const { CAN, USA, ...rest } = result.reduce(
        (acc, cur) => ({ ...acc, [cur.cca3]: { value: cur.cca3, label: cur.flag, name: cur.name.common } }),
        {} as Record<string, Record<string, string>>
      );
      return [CAN, USA, ...orderBy(Object.values(rest), ["name"], ["asc"])];
    },
  });

  const fetchIndexAndPrefillForm = async () => {
    const allRoles = (await fetchRolesIndex(query)) as Role[];
    if (!isEditingUser) return;
    const user = await fetchUserDetails(userID);
    if (user && allRoles) {
      const firstName = get(user, "first_name", "");
      const lastName = get(user, "last_name", "");
      const phoneNumber = get(user, "phone_number", "") as string;

      const roleName = get(user, "organization_role", "");
      const matchingRole = allRoles?.find((role: Role) => role.name === roleName);
      const roleID = get(matchingRole, "id", "");
      const subrole = get(user, "subrole", "");
      const positionId = get(user, "position_id", "");

      const driverUser = user.driver_user;
      setFirstName(firstName);
      setLastName(lastName);
      setPhoneNumber(phoneNumber as string);
      setRoleID(roleID);
      setSubrole(subrole);
      setPositionId(positionId);
      setCompanyName(driverUser?.fleet_operator || "");
      setSelectedServiceAreas(driverUser?.service_areas || []);
      setDspType(driverUser?.driver_type || "");
      setDriverUserId(driverUser?.id || "");
    }
  };

  // eslint-disable-next-line
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchIndexAndPrefillForm();
    fetchOrganizationDetails(organizationID);
  }, []);

  const error = organizationUserDetails.error || rolesIndex.error || userDetails.error;

  useEffect(() => {
    if (error) {
      notification.error({
        message: "Error",
        description: error,
      });
    }
  }, [error]);

  useEffect(() => {
    setInputError("");
  }, [firstName, lastName, email, password, phoneNumber, roleID, companyName]);

  if (rolesIndex.loading || userDetails.loading) return <Loader />;
  if (!rolesIndex.data) return null;

  const renderRoleDropdown = () => {
    const availableRoles = rolesIndex?.data || [];

    const options = availableRoles.map((role: Role) => {
      const id = get(role, "id", "");
      const name = get(role, "name", "");
      return (
        <Select.Option key={id} value={id}>
          {startCase(name)}
        </Select.Option>
      );
    });

    return (
      <div className="user-create-input">
        <div className="form-label">Role</div>
        <Select value={roleID || "Select Role..."} onChange={(value) => setRoleID(value)}>
          {options}
        </Select>
      </div>
    );
  };

  const renderSubRoleDropdown = () => {
    if (!isGoBolt) return null;

    const options = User.getSubroles();

    return (
      <div className="user-create-input">
        <div className="form-label">Subrole</div>
        <Select value={subrole || "Select Subrole..."} onChange={(value) => setSubrole(value)}>
          {options.map((opt) => (
            <Select.Option value={opt.value} key={opt.value}>
              {opt.name}
            </Select.Option>
          ))}
        </Select>
      </div>
    );
  };

  const renderServiceAreaDropdown = () => {
    if (!shouldShowDriverAttributes) return null;

    if (!serviceArea.data) return null;
    const options = serviceArea.data.map((serviceArea) => {
      return { value: serviceArea.name, title: upperCase(serviceArea.name) };
    });

    return (
      <div className="user-create-input">
        <div className="form-label">Service Area</div>
        <TreeSelect
          style={{ width: "100%" }}
          value={selectedServiceAreas}
          dropdownStyle={{ maxHeight: 400, overflow: "auto", paddingLeft: "0px" }}
          placeholder="Please select"
          treeCheckable
          multiple
          showSearch
          showCheckedStrategy={TreeSelect.SHOW_ALL}
          onChange={(value) => setSelectedServiceAreas(value)}
          treeData={options}
        />
      </div>
    );
  };

  const renderDspTypeDropdown = () => {
    if (!shouldShowDriverAttributes) return null;

    const dspTypes = ["fleet_operator", "independent_operator"];

    return (
      <div className="user-create-input">
        <div className="form-label">Driver Type</div>
        <Select value={dspType || "Select Driver Type..."} onChange={(value) => setDspType(value)} disabled>
          {dspTypes.map((type) => (
            <Select.Option value={type} key={type}>
              {startCase(type)}
            </Select.Option>
          ))}
        </Select>
      </div>
    );
  };

  const renderCompanyNameDropdown = () => {
    if (!shouldShowDriverAttributes) return null;

    const options = uniq(fleetOperatorList).map((name) => {
      return { value: name };
    });

    return (
      <AutoComplete
        style={{ width: "100%" }}
        options={options}
        value={companyName}
        onSearch={(value) => setCompanyName(value)}
        onSelect={(value) => setCompanyName(value)}
        filterOption={(inputValue, option) => option?.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1}
      />
    );
  };

  const isVerified = (segment: string | null) => {
    switch (segment) {
      case firstName:
        return firstName.length >= 2;
      case lastName:
        return lastName.length >= 2;
      case email:
        return /\S+@\S+\.\S+/.test(email);
      case password:
        return password.length >= 6;
      case phoneNumber:
        return !!phoneNumber && phoneValidator(phoneNumber, { country: phonePrefix }).isValid;
      case roleID:
        return !isEmpty(roleID);
      default:
        return false;
    }
  };

  const renderAlertBanner = () => {
    if (inputError) {
      return <Alert message={inputError} type="error" banner />;
    }
  };

  const submitNewUser = async () => {
    if (subrole === "mover" || subrole === "driver") {
      return notification.error({
        message: "Cannot create driver",
        description: 'Please use the "Invite Driver" button on Organization Screen to invite drivers.',
      });
    }

    const { organization_id } = params;
    const body = {
      user: {
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        phone_number: phoneNumber ? phoneValidator(phoneNumber).phoneNumber : null,
        role_id: roleID,
        role: isGoBolt ? "is_admin" : "is_fulfillment_client",
        organization_id,
        subrole: subrole,
        position_id: positionId || undefined,
      },
    };
    const user = await createOrganizationUser(body);
    if (user) {
      history.push(`/organizations/${organizationID}`);
    }
  };

  const updateExistingDriverUser = async () => {
    const param = {
      id: driverUserId,
      companyName: companyName,
      serviceAreas: selectedServiceAreas,
      driverType: dspType,
    };
    return await updateDriverUser(param);
  };

  const updateExistingUser = async () => {
    const body = {
      user: {
        first_name: firstName,
        last_name: lastName,
        phone_number: phoneNumber ? phoneValidator(phoneNumber).phoneNumber : null,
        role_id: roleID,
        role: isGoBolt ? "is_admin" : "is_fulfillment_client",
        subrole: isGoBolt ? subrole : undefined,
        position_id: positionId || undefined,
      },
    };

    const updatedUser = await updateUserDetails(userID, body);
    if (!updatedUser) return;

    if (isDriverUserExist) {
      const updatedDriverUser = await updateExistingDriverUser();
      if (!updatedDriverUser) return;
    }

    notification.success({ message: "User updated successfully" });
    history.push(`/organizations/${organizationID}`);
  };

  const onSubmit = () => {
    if (!enableCreateUser.data?.state) {
      return notification.error({
        message: "Permission Denied",
        description: "You don't have permission to create/update users. Please contact IT for support.",
      });
    }

    if (!isVerified(firstName)) return setInputError("First name must be at least 2 characters.");
    if (!isVerified(lastName)) return setInputError("Last name must be at least 2 characters.");
    if (!isVerified(phoneNumber)) return setInputError("Please enter a valid phone number.");
    if (!isVerified(roleID)) return setInputError("Please select a role for this user.");

    if (isEditingUser) {
      updateExistingUser();
    } else {
      if (!isVerified(email)) {
        return setInputError("Please make sure email is correct.");
      }
      if (!isVerified(password)) {
        return setInputError("Password must be longer than 6 characters.");
      }

      submitNewUser();
    }
  };

  const renderSubmitButton = () => {
    return (
      <div className="create-user-btn-wrapper">
        <Button
          className="create-user-btn"
          onClick={onSubmit}
          loading={organizationUserDetails.loading || isUpdateDriverUserLoading}
          icon={<UserAddOutlined />}
        >
          {isEditingUser ? "Submit" : "Create User"}
        </Button>
      </div>
    );
  };

  const renderLoginCredentials = () => {
    if (isEditingUser) return;
    return (
      <>
        <InputWithVerification value={email} setValue={setEmail} isVerified={isVerified(email)} labelText="Email" />
        <InputWithVerification
          value={password}
          setValue={setPassword}
          isVerified={isVerified(password)}
          labelText="Password"
        />
      </>
    );
  };

  const renderPhonePrefix = () => (
    <Select
      style={{ width: "min-content" }}
      loading={countryIndex.isLoading}
      value={phonePrefix}
      onChange={setPhonePrefix}
    >
      {countryIndex.data?.map((c) => (
        <Select.Option key={c.value} value={c.value} title={c.name}>
          {c.label}
        </Select.Option>
      ))}
    </Select>
  );

  return (
    <Spin spinning={organizationDetails.loading || isUpdateDriverUserLoading}>
      <div id="organization-user-create-page" className="page-container">
        <FulfillmentBackButton urlPath={`/organizations/${organizationID}/users`} label="Users" />
        <div className="page-header">{isEditingUser ? "Edit" : "Add New"} User</div>
        <div className="user-create-form">
          {renderAlertBanner()}
          <InputWithVerification
            value={firstName}
            setValue={setFirstName}
            isVerified={isVerified(firstName)}
            labelText="First Name"
          />
          <InputWithVerification
            value={lastName}
            setValue={setLastName}
            isVerified={isVerified(lastName)}
            labelText="Last Name"
          />
          {renderLoginCredentials()}
          <InputWithVerification
            prefix={renderPhonePrefix()}
            value={phoneNumber || ""}
            setValue={setPhoneNumber}
            isVerified={isVerified(phoneNumber)}
            labelText="Phone Number"
          />
          <InputWithVerification
            value={positionId}
            setValue={setPositionId}
            isVerified={false}
            labelText="Employee ID"
          />
          {renderCompanyNameDropdown()}

          {renderServiceAreaDropdown()}
          {renderDspTypeDropdown()}

          {renderRoleDropdown()}
          {renderSubRoleDropdown()}
          {renderSubmitButton()}
        </div>
      </div>
    </Spin>
  );
};

export default OrganizationUserCreateEditPage;
